import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CABIN_CLASS_OPTIONS = [
  { value: 'economy', label: 'Economy' },
  { value: 'premium_economy', label: 'Premium Economy' },
  { value: 'business', label: 'Business' },
  { value: 'first', label: 'First Class' },
];

const TRIP_TYPE_OPTIONS = [
  { value: 'one_way', label: 'One Way' },
  { value: 'round_trip', label: 'Round Trip' },
];

const PassengerCounter = ({ label, description, value, onChange, min = 0, max = 9 }) => (
  <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
    <div>
      <p className="text-sm font-medium text-foreground">{label}</p>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <Icon name="Minus" size={14} />
      </button>
      <span className="w-6 text-center text-sm font-semibold text-foreground">{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <Icon name="Plus" size={14} />
      </button>
    </div>
  </div>
);

const FlightSearchForm = ({ onSearch, loading }) => {
  const [tripType, setTripType] = useState('one_way');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [cabinClass, setCabinClass] = useState('economy');
  const [showPassengers, setShowPassengers] = useState(false);
  const [passengers, setPassengers] = useState({ adults: 1, children: 0, infants: 0 });
  const [errors, setErrors] = useState({});

  const totalPassengers = (passengers?.adults || 0) + (passengers?.children || 0) + (passengers?.infants || 0);

  const passengerLabel = totalPassengers === 1
    ? '1 Passenger'
    : `${totalPassengers} Passengers`;

  const validate = () => {
    const newErrors = {};
    if (!origin?.trim()) newErrors.origin = 'Origin airport code is required';
    else if (!/^[A-Z]{3}$/?.test(origin?.trim()?.toUpperCase())) newErrors.origin = 'Enter a valid 3-letter IATA code (e.g. JFK)';
    if (!destination?.trim()) newErrors.destination = 'Destination airport code is required';
    else if (!/^[A-Z]{3}$/?.test(destination?.trim()?.toUpperCase())) newErrors.destination = 'Enter a valid 3-letter IATA code (e.g. LHR)';
    if (origin?.trim()?.toUpperCase() === destination?.trim()?.toUpperCase()) newErrors.destination = 'Origin and destination cannot be the same';
    if (!departureDate) newErrors.departureDate = 'Departure date is required';
    if (tripType === 'round_trip' && !returnDate) newErrors.returnDate = 'Return date is required';
    if (tripType === 'round_trip' && returnDate && departureDate && returnDate < departureDate) newErrors.returnDate = 'Return date must be after departure date';
    if (passengers?.adults < 1) newErrors.passengers = 'At least 1 adult is required';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validate()) return;
    onSearch?.({
      origin: origin?.trim()?.toUpperCase(),
      destination: destination?.trim()?.toUpperCase(),
      departureDate,
      returnDate: tripType === 'round_trip' ? returnDate : null,
      passengers,
      cabinClass,
    });
  };

  const swapAirports = () => {
    const temp = origin;
    setOrigin(destination);
    setDestination(temp);
  };

  const today = new Date()?.toISOString()?.split('T')?.[0];

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl p-6 shadow-card">
      {/* Trip Type */}
      <div className="flex gap-2 mb-6">
        {TRIP_TYPE_OPTIONS?.map((opt) => (
          <button
            key={opt?.value}
            type="button"
            onClick={() => setTripType(opt?.value)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
              tripType === opt?.value
                ? 'text-white shadow-sm'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            style={tripType === opt?.value ? { background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' } : {}}
          >
            {opt?.label}
          </button>
        ))}
      </div>
      {/* Origin / Destination Row */}
      <div className="flex items-start gap-2 mb-4">
        <div className="flex-1">
          <Input
            label="From"
            placeholder="e.g. JFK"
            value={origin}
            onChange={(e) => {
              setOrigin(e?.target?.value?.toUpperCase());
              if (errors?.origin) setErrors((prev) => ({ ...prev, origin: '' }));
            }}
            maxLength={3}
            error={errors?.origin}
            description="3-letter IATA airport code"
            className="uppercase"
          />
        </div>
        <button
          type="button"
          onClick={swapAirports}
          className="mt-7 h-10 w-10 flex-shrink-0 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          title="Swap airports"
        >
          <Icon name="ArrowLeftRight" size={16} />
        </button>
        <div className="flex-1">
          <Input
            label="To"
            placeholder="e.g. LHR"
            value={destination}
            onChange={(e) => {
              setDestination(e?.target?.value?.toUpperCase());
              if (errors?.destination) setErrors((prev) => ({ ...prev, destination: '' }));
            }}
            maxLength={3}
            error={errors?.destination}
            description="3-letter IATA airport code"
            className="uppercase"
          />
        </div>
      </div>
      {/* Dates Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <Input
          label="Departure Date"
          type="date"
          value={departureDate}
          min={today}
          onChange={(e) => {
            setDepartureDate(e?.target?.value);
            if (errors?.departureDate) setErrors((prev) => ({ ...prev, departureDate: '' }));
          }}
          error={errors?.departureDate}
        />
        {tripType === 'round_trip' && (
          <Input
            label="Return Date"
            type="date"
            value={returnDate}
            min={departureDate || today}
            onChange={(e) => {
              setReturnDate(e?.target?.value);
              if (errors?.returnDate) setErrors((prev) => ({ ...prev, returnDate: '' }));
            }}
            error={errors?.returnDate}
          />
        )}
      </div>
      {/* Passengers & Cabin Class Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {/* Passengers Dropdown */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Passengers</label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowPassengers(!showPassengers)}
              className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <div className="flex items-center gap-2">
                <Icon name="Users" size={15} className="text-muted-foreground" />
                <span>{passengerLabel}</span>
              </div>
              <Icon name={showPassengers ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
            </button>
            {showPassengers && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-xl shadow-dropdown p-4 animate-fade-in">
                <PassengerCounter
                  label="Adults"
                  description="Age 12+"
                  value={passengers?.adults}
                  onChange={(v) => setPassengers((p) => ({ ...p, adults: v }))}
                  min={1}
                />
                <PassengerCounter
                  label="Children"
                  description="Age 2–11"
                  value={passengers?.children}
                  onChange={(v) => setPassengers((p) => ({ ...p, children: v }))}
                />
                <PassengerCounter
                  label="Infants"
                  description="Under 2 (no seat)"
                  value={passengers?.infants}
                  onChange={(v) => setPassengers((p) => ({ ...p, infants: v }))}
                  max={passengers?.adults}
                />
                <Button
                  type="button"
                  size="sm"
                  className="w-full mt-3"
                  onClick={() => setShowPassengers(false)}
                >
                  Done
                </Button>
              </div>
            )}
          </div>
          {errors?.passengers && <p className="text-sm text-destructive">{errors?.passengers}</p>}
        </div>

        {/* Cabin Class */}
        <Select
          label="Cabin Class"
          options={CABIN_CLASS_OPTIONS}
          value={cabinClass}
          onChange={setCabinClass}
        />
      </div>
      {/* Submit */}
      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={loading}
        iconName="Search"
        iconPosition="left"
        className="text-white"
        style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
      >
        {loading ? 'Searching Flights...' : 'Search Flights'}
      </Button>
    </form>
  );
};

export default FlightSearchForm;
